import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, MouseEvent } from 'react';
import { keyframes, styled } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

type UploadStatus = 'idle' | 'dragging' | 'uploading' | 'paused' | 'cancelled' | 'completed';

interface UploadDropZoneProps {
  /** Fires after the simulated upload animation reaches 100%. */
  onComplete: (file: File) => void;
  /** Fires when the user removes the currently uploaded file. */
  onDismiss?: () => void;
  /** Accepted MIME / extension list for the underlying input. */
  accept?: string;
  /** Headline shown in the idle state. */
  title?: string;
  /** Supporting copy under the idle headline. */
  subtitle?: string;
  /** File-format chips under the idle copy. */
  formatTags?: string[];
}

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const iconPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const borderColor = (s: UploadStatus): string => {
  if (s === 'dragging' || s === 'paused') {return '#BC92F8';}
  if (s === 'uploading') {return '#2B3501';}
  if (s === 'cancelled') {return '#E24B4A';}
  if (s === 'completed') {return '#2B7A0B';}
  return '#CCCCCC';
};

const backgroundFor = (s: UploadStatus): string => {
  if (s === 'dragging') {return 'rgba(188,146,248,0.05)';}
  if (s === 'cancelled') {return 'rgba(226,75,74,0.03)';}
  if (s === 'completed') {return 'rgba(43,122,11,0.03)';}
  return '#fff';
};

const cursorFor = (s: UploadStatus): string =>
  s === 'idle' || s === 'dragging' ? 'pointer' : 'default';

const Zone = styled('div', { shouldForwardProp: (p) => p !== 'status' })<{ status: UploadStatus }>`
  border: 2px dashed ${({ status }) => borderColor(status)};
  border-radius: 16px;
  background: ${({ status }) => backgroundFor(status)};
  padding: 40px 28px;
  text-align: center;
  cursor: ${({ status }) => cursorFor(status)};
  transition: border-color 0.2s, background 0.2s;
  font-family: 'Inter', sans-serif;
`;

const IdleWrap = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DropIcon = styled('div', { shouldForwardProp: (p) => p !== 'dragging' })<{
  dragging?: boolean;
}>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ dragging }) => (dragging ? 'rgba(188,146,248,0.12)' : '#F5F5F3')};
  border: 1.5px solid ${({ dragging }) => (dragging ? '#BC92F8' : '#E6E6E2')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${({ dragging }) => (dragging ? '#BC92F8' : '#959A80')};
  transition: all 0.2s;
`;

const DropTitle = styled('div')`
  font-size: 16px;
  font-weight: 600;
  color: #1f1f1f;
  margin-bottom: 6px;
`;

const DropSub = styled('div')`
  font-size: 12px;
  color: #959a80;
  margin-bottom: 20px;
`;

const BrowseBtn = styled('button')`
  background: #f5f5f3;
  border: 1.5px solid #e6e6e2;
  color: #1f1f1f;
  border-radius: 50px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.13s;
  &:hover {
    border-color: #aaaaaa;
    background: #ededed;
  }
`;

const FormatTags = styled('div')`
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 14px;
`;

const FormatTag = styled('span')`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  font-weight: 700;
  background: #f5f5f3;
  border: 1px solid #e6e6e2;
  color: #959a80;
  padding: 3px 8px;
  border-radius: 4px;
  letter-spacing: 0.05em;
`;

const UploadRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const fileIconBg = (s: UploadStatus): string => {
  if (s === 'uploading') {return 'rgba(43,53,1,0.06)';}
  if (s === 'paused') {return 'rgba(188,146,248,0.1)';}
  if (s === 'cancelled') {return 'rgba(226,75,74,0.07)';}
  if (s === 'completed') {return 'rgba(43,122,11,0.08)';}
  return '#F5F5F3';
};

const fileIconBorder = (s: UploadStatus): string => {
  if (s === 'uploading') {return '#2B3501';}
  if (s === 'paused') {return '#BC92F8';}
  if (s === 'cancelled') {return '#E24B4A';}
  if (s === 'completed') {return '#2B7A0B';}
  return '#E6E6E2';
};

const fileIconColor = (s: UploadStatus): string => {
  if (s === 'uploading') {return '#2B3501';}
  if (s === 'paused') {return '#7B47CC';}
  if (s === 'cancelled') {return '#A32D2D';}
  if (s === 'completed') {return '#2B7A0B';}
  return '#959A80';
};

const FileIcon = styled('div', { shouldForwardProp: (p) => p !== 'status' })<{
  status: UploadStatus;
}>`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: ${({ status }) => fileIconBg(status)};
  border: 1.5px solid ${({ status }) => fileIconBorder(status)};
  color: ${({ status }) => fileIconColor(status)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  animation: ${({ status }) => (status === 'uploading' ? iconPulse : 'none')} 1.4s ease-in-out
    infinite;
`;

const UploadBody = styled('div')`
  flex: 1;
  min-width: 0;
`;

const UploadTop = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
`;

const Filename = styled('div')`
  font-size: 13px;
  font-weight: 600;
  color: #1f1f1f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 55%;
  text-align: left;
`;

const UploadRight = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const StatusRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const dotColor = (s: UploadStatus): string => {
  if (s === 'paused') {return '#BC92F8';}
  if (s === 'cancelled') {return '#E24B4A';}
  if (s === 'completed') {return '#2B7A0B';}
  return '#2B3501';
};

const StatusDot = styled('div', { shouldForwardProp: (p) => p !== 'status' })<{
  status: UploadStatus;
}>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ status }) => dotColor(status)};
  flex-shrink: 0;
  transition: background 0.2s;
  animation: ${({ status }) => (status === 'uploading' ? blink : 'none')} 1.2s ease-in-out infinite;
`;

const labelColor = (s: UploadStatus): string => {
  if (s === 'paused') {return '#7B47CC';}
  if (s === 'cancelled') {return '#A32D2D';}
  return '#2B7A0B';
};

const StatusLabel = styled('span', { shouldForwardProp: (p) => p !== 'status' })<{
  status: UploadStatus;
}>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${({ status }) => labelColor(status)};
  transition: color 0.2s;
`;

const pctColor = (s: UploadStatus): string => {
  if (s === 'paused') {return '#7B47CC';}
  if (s === 'cancelled') {return '#A32D2D';}
  if (s === 'completed') {return '#2B7A0B';}
  return '#1F1F1F';
};

const Pct = styled('div', { shouldForwardProp: (p) => p !== 'status' })<{ status: UploadStatus }>`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: ${({ status }) => pctColor(status)};
  transition: color 0.2s;
  min-width: 36px;
  text-align: right;
`;

const Track = styled('div')`
  flex: 1;
  height: 5px;
  background: #e6e6e2;
  border-radius: 10px;
  overflow: hidden;
`;

const fillColor = (s: UploadStatus): string => {
  if (s === 'paused') {return '#BC92F8';}
  if (s === 'cancelled') {return '#E24B4A';}
  if (s === 'completed') {return '#2B7A0B';}
  return '#2B3501';
};

const TrackFill = styled('div', { shouldForwardProp: (p) => p !== 'status' })<{
  status: UploadStatus;
}>`
  height: 100%;
  background: ${({ status }) => fillColor(status)};
  border-radius: 10px;
  transition: width 0.1s linear, background 0.3s;
`;

const UploadMeta = styled('div')`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 6px;
`;

const MetaText = styled('span')`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  color: #aaaaaa;
`;

const CtrlGroup = styled('div')`
  display: flex;
  gap: 7px;
  flex-shrink: 0;
`;

interface CtrlBtnProps {
  variant: 'pause' | 'cancel' | 'resume';
}

const CtrlBtn = styled('button', { shouldForwardProp: (p) => p !== 'variant' })<CtrlBtnProps>`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  border: 1.5px solid
    ${({ variant }) => (variant === 'resume' ? 'rgba(188,146,248,0.5)' : '#E6E6E2')};
  background: ${({ variant }) => (variant === 'resume' ? 'rgba(188,146,248,0.08)' : '#fff')};
  color: ${({ variant }) => (variant === 'resume' ? '#7B47CC' : '#1F1F1F')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  padding: 0;
  &:hover {
    ${({ variant }) =>
      variant === 'pause'
        ? `border-color: #BC92F8; color: #7B47CC; background: rgba(188,146,248,0.08);`
        : ''}
    ${({ variant }) =>
      variant === 'cancel'
        ? `border-color: #E24B4A; color: #A32D2D; background: rgba(226,75,74,0.06);`
        : ''}
    ${({ variant }) =>
      variant === 'resume' ? `border-color: #7B47CC; background: rgba(188,146,248,0.16);` : ''}
  }
`;

const CancelledActions = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  justify-content: center;
`;

const RetryBtn = styled('button')`
  background: #2b3501;
  color: #fff;
  border: none;
  border-radius: 50px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.13s;
  &:hover {
    opacity: 0.85;
  }
`;

const DismissBtn = styled('button')`
  background: transparent;
  color: #959a80;
  border: 1.5px solid #e6e6e2;
  border-radius: 50px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  transition: all 0.13s;
  &:hover {
    border-color: #999;
    color: #1f1f1f;
  }
`;

const HiddenInput = styled('input')`
  display: none;
`;

const truncate = (name: string): string =>
  name.length > 30 ? `${name.slice(0, 28)}…` : name;

const UploadDropZone: React.FC<UploadDropZoneProps> = ({
  onComplete,
  onDismiss,
  accept = 'application/pdf',
  title = 'Drop your PDF here',
  subtitle = 'Fresh document or one already signed — drop or browse to upload',
  formatTags = ['PDF'],
}) => {
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [filename, setFilename] = useState('');
  const [speed, setSpeed] = useState('— MB/s');
  const [eta, setEta] = useState('Calculating…');

  const statusRef = useRef<UploadStatus>('idle');
  const progressRef = useRef(0);
  const pendingFileRef = useRef<File | null>(null);
  const intervalRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const setUploadStatus = useCallback((next: UploadStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const updateProgress = useCallback((next: number) => {
    progressRef.current = next;
    setProgress(next);
  }, []);

  const clearTick = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Tracks `onComplete` / `onDismiss` without restarting timers on parent re-renders.
  const onCompleteRef = useRef(onComplete);
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  const resetToIdle = useCallback(() => {
    pendingFileRef.current = null;
    setFilename('');
    setUploadStatus('idle');
    updateProgress(0);
    setSpeed('— MB/s');
    setEta('Calculating…');
  }, [setUploadStatus, updateProgress]);

  const finishUpload = useCallback(() => {
    clearTick();
    const file = pendingFileRef.current;
    setUploadStatus('completed');
    updateProgress(100);
    setSpeed('');
    setEta('');
    if (file) {onCompleteRef.current(file);}
  }, [clearTick, setUploadStatus, updateProgress]);

  const runTicker = useCallback(() => {
    clearTick();
    intervalRef.current = window.setInterval(() => {
      if (statusRef.current !== 'uploading') {return;}
      const next = Math.min(progressRef.current + Math.random() * 6 + 2, 100);
      updateProgress(next);
      const spd = (Math.random() * 3 + 1.5).toFixed(1);
      const remaining = Math.round((100 - next) / 8);
      setSpeed(`${spd} MB/s`);
      setEta(remaining > 0 ? `ETA ${remaining}s` : 'Almost…');
      if (next >= 100) {
        clearTick();
        window.setTimeout(finishUpload, 300);
      }
    }, 80);
  }, [clearTick, finishUpload, updateProgress]);

  const startUpload = useCallback(
    (file: File) => {
      if (statusRef.current === 'uploading' || statusRef.current === 'paused') {return;}
      pendingFileRef.current = file;
      setFilename(truncate(file.name));
      updateProgress(0);
      setSpeed('— MB/s');
      setEta('Calculating…');
      setUploadStatus('uploading');
      runTicker();
    },
    [runTicker, setUploadStatus, updateProgress],
  );

  useEffect(() => () => clearTick(), [clearTick]);

  const triggerBrowse = useCallback(() => inputRef.current?.click(), []);

  const handleZoneClick = useCallback(() => {
    if (statusRef.current === 'idle' || statusRef.current === 'dragging') {triggerBrowse();}
  }, [triggerBrowse]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {startUpload(file);}
      // Allow re-selecting the same file later.
      e.target.value = '';
    },
    [startUpload],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (statusRef.current === 'idle' || statusRef.current === 'dragging') {
      setUploadStatus('dragging');
    }
  }, [setUploadStatus]);

  const handleDragLeave = useCallback(() => {
    if (statusRef.current === 'dragging') {setUploadStatus('idle');}
  }, [setUploadStatus]);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (statusRef.current === 'dragging') {setUploadStatus('idle');}
      if (statusRef.current === 'uploading' || statusRef.current === 'paused') {return;}
      const file = e.dataTransfer.files?.[0];
      if (file) {startUpload(file);}
    },
    [setUploadStatus, startUpload],
  );

  const togglePause = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (statusRef.current === 'uploading') {
        setUploadStatus('paused');
        setSpeed('— MB/s');
        setEta('Paused');
      } else if (statusRef.current === 'paused') {
        setUploadStatus('uploading');
      }
    },
    [setUploadStatus],
  );

  const cancelUpload = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      clearTick();
      setUploadStatus('cancelled');
      setSpeed('');
      setEta('');
    },
    [clearTick, setUploadStatus],
  );

  const retryUpload = useCallback(() => {
    const file = pendingFileRef.current;
    setUploadStatus('idle');
    if (file) {startUpload(file);}
  }, [setUploadStatus, startUpload]);

  const dismissCancel = useCallback(() => {
    resetToIdle();
  }, [resetToIdle]);

  /** "Remove" action shown while a completed upload is on screen. */
  const removeCompleted = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      resetToIdle();
      onDismissRef.current?.();
    },
    [resetToIdle],
  );

  /** "Replace" action shown while a completed upload is on screen. */
  const replaceCompleted = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      resetToIdle();
      onDismissRef.current?.();
      triggerBrowse();
    },
    [resetToIdle, triggerBrowse],
  );

  const showIdle = status === 'idle' || status === 'dragging';
  const showControls =
    status === 'uploading' || status === 'paused' || status === 'completed';
  const isCompleted = status === 'completed';

  return (
    <Zone
      status={status}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleZoneClick}
    >
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
      />

      {showIdle && (
        <IdleWrap>
          <DropIcon dragging={status === 'dragging'}>
            <CloudUploadOutlinedIcon sx={{ fontSize: 28 }} />
          </DropIcon>
          <DropTitle>{title}</DropTitle>
          <DropSub>{subtitle}</DropSub>
          <BrowseBtn
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              triggerBrowse();
            }}
          >
            <FolderOpenOutlinedIcon sx={{ fontSize: 14 }} />
            Browse Files
          </BrowseBtn>
          {formatTags.length > 0 && (
            <FormatTags>
              {formatTags.map((t) => (
                <FormatTag key={t}>{t}</FormatTag>
              ))}
            </FormatTags>
          )}
        </IdleWrap>
      )}

      {!showIdle && (
        <>
          <UploadRow>
            <FileIcon status={status}>
              <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />
            </FileIcon>
            <UploadBody>
              <UploadTop>
                <Filename title={filename}>{filename}</Filename>
                <UploadRight>
                  <StatusRow>
                    <StatusDot status={status} />
                    <StatusLabel status={status}>
                      {status === 'paused'
                        ? 'Paused'
                        : status === 'cancelled'
                          ? 'Cancelled'
                          : status === 'completed'
                            ? 'Uploaded'
                            : 'Uploading'}
                    </StatusLabel>
                  </StatusRow>
                  <Pct status={status}>{Math.round(progress)}%</Pct>
                </UploadRight>
              </UploadTop>
              <Track>
                <TrackFill status={status} style={{ width: `${Math.round(progress)}%` }} />
              </Track>
              {(status === 'uploading' || status === 'paused') && (
                <UploadMeta>
                  <MetaText>{speed}</MetaText>
                  <MetaText>{eta}</MetaText>
                </UploadMeta>
              )}
              {isCompleted && (
                <UploadMeta>
                  <MetaText>Ready to sign</MetaText>
                </UploadMeta>
              )}
            </UploadBody>

            {showControls && !isCompleted && (
              <CtrlGroup>
                <CtrlBtn
                  type="button"
                  variant={status === 'paused' ? 'resume' : 'pause'}
                  onClick={togglePause}
                  aria-label={status === 'paused' ? 'Resume upload' : 'Pause upload'}
                >
                  {status === 'paused' ? (
                    <PlayArrowIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <PauseIcon sx={{ fontSize: 18 }} />
                  )}
                </CtrlBtn>
                <CtrlBtn
                  type="button"
                  variant="cancel"
                  onClick={cancelUpload}
                  aria-label="Cancel upload"
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </CtrlBtn>
              </CtrlGroup>
            )}

            {isCompleted && (
              <CtrlGroup>
                <CtrlBtn
                  type="button"
                  variant="pause"
                  onClick={replaceCompleted}
                  aria-label="Replace file"
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </CtrlBtn>
                <CtrlBtn
                  type="button"
                  variant="cancel"
                  onClick={removeCompleted}
                  aria-label="Remove uploaded file"
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </CtrlBtn>
              </CtrlGroup>
            )}
          </UploadRow>

          {status === 'cancelled' && (
            <CancelledActions>
              <RetryBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  retryUpload();
                }}
              >
                <RefreshIcon sx={{ fontSize: 14 }} />
                Retry Upload
              </RetryBtn>
              <DismissBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissCancel();
                }}
              >
                Dismiss
              </DismissBtn>
            </CancelledActions>
          )}
        </>
      )}
    </Zone>
  );
};

export default UploadDropZone;
